import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "clsx";
import { isFunction } from "../../utils/helpers";
import useT from "../../hooks/useT";
import { InputCustomChangeEventHandler } from "../../types";

type InternalRefObject =
  | React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>
  | React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;

export interface InputCpfProps {
  /** Allows using `...inputProps` to pass HTMLInputElement/HTMLTextareaElement props to the input/text element itself */
  [propName: string]: any;
  id: string;
  name: string;
  type: string;
  value: string | null;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  placeholder?: string;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  onChange?: InputCustomChangeEventHandler;
  onClear?: React.MouseEventHandler;
  onCustomError?: (error: string) => void;
  onClearCustomError?: () => void;
}

export function formatInput(
  input: string,
  keyDown: string,
  originalValue: string
): any {
  let formatted = "";
  if (keyDown === "Backspace") {
    return (formatted = originalValue);
  }
  for (let i = 0; i < input.length; i++) {
    formatted += input[i];
    if (i > 0 && (i + 1) % 9 === 0) {
      formatted += "-";
    }
    if (i > 0 && (i + 1) % 3 === 0 && i < 8) {
      formatted += ".";
    }
  }
  return formatted;
}

export const InputCpfField = React.forwardRef(
  (
    {
      id,
      name,
      type,
      value,
      disabled,
      clearable,
      className,
      placeholder,
      onFocus,
      onBlur,
      onChange,
      onClear,
      onClearCustomError,
      ...inputProps
    }: InputCpfProps,
    forwardedRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { t } = useT();
    let _value = value ? value : "";
    const [keyDown, setKeyDown] = useState("");
    const [val, setVal] = useState("");
    const internalRef = useRef<HTMLInputElement | null>(null);
    const ref: InternalRefObject =
      !forwardedRef || isFunction(forwardedRef)
        ? internalRef
        : (forwardedRef as InternalRefObject);
    const setOnChange = (val: string) => {
      if (onChange) {
        onChange(val || null, "CpfCnpjCode");
      }
    };

    const handleTextEntered = async (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      let value = event.target.value.replace(/[^\d\b]/g, "");
      value = formatInput(value, keyDown, event.target.value);
      setVal(value);
      setOnChange(value);
    };
    useEffect(() => {}, [val, keyDown]);

    const handleOnKeyDown = (event: any) => {
      setKeyDown(event.key);
    };
    return (
      <div
        className={cn(
          "input input-text",
          className,
          !!value && "has-value",
          !!clearable && "is-clearable"
        )}
      >
        <input
          {...inputProps}
          type="text"
          id={id}
          name={name}
          value={_value}
          autoComplete="off"
          disabled={disabled}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={handleTextEntered}
          className="input-control input-text-control"
          placeholder={placeholder}
          ref={ref as React.RefObject<HTMLInputElement>}
          onKeyDown={handleOnKeyDown}
        />
      </div>
    );
  }
);

InputCpfField.displayName = "InputCpfField";

export default InputCpfField;
